import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withoutForbiddenElevation } from '@application/data-access';
import type {
  IRegistrationHealthComment,
  TRegistrationParqQuestion,
} from '../entities/registration-candidate.model';

/**
 * Takes the health answers of a link registration to the person, once the
 * counter has created it: a comment on the profile and the anamnesis of the
 * physical assessment.
 *
 * Two plain requests, written here and not sent through the libs that own
 * those screens:
 *
 * - The profile's data lib already imports this one. Reaching its
 *   `ProfileService` from here would close a cycle between the two.
 * - The assessment's store posts the evaluator's whole form behind a permission
 *   prompt, and its lib has no `type:` tag, so a data lib may not depend on it.
 *   The counter writes seven answers and asks nobody.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationHealthService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  /**
   * Writes the comment with the answers on the person's profile.
   *
   * The same request the profile screen makes for a comment typed there. Not
   * `PersonService.createPersonComment`, which posts to the user service.
   *
   * A 403 here may still ask for a temporary elevation, as any comment does.
   */
  public addProfileComment(
    personId: string,
    comment: IRegistrationHealthComment
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiUrl.personService}/Comment`,
      { ...comment, TargetPersonId: personId },
      { withCredentials: true }
    );
  }

  /**
   * Writes the seven answers to the person's own anamnesis row.
   *
   * - `IsOwner: true` keys the row by the person and creates no assessment.
   *   Without it the service opens an assessment nobody carried out.
   * - `AhaOther: true` is what the assessment screen works out when no AHA item
   *   is marked, which is the case here. With `false` the evaluator would open
   *   the next assessment to "medical supervision is required", even when the
   *   seven answers are no.
   *
   * No temporary elevation on a 403: nobody at the counter is to be asked for a
   * supervisor's password over this. The error reaches the store, which goes
   * on with the comment alone.
   */
  public addOwnerAnamnesis(
    personId: string,
    answers: Record<TRegistrationParqQuestion, boolean>
  ): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiUrl.physical_assessment_service}/Anamnesis`,
      {
        PersonId: personId,
        IsOwner: true,
        AhaOther: true,
        ParQ1: answers.ParQ1,
        ParQ2: answers.ParQ2,
        ParQ3: answers.ParQ3,
        ParQ4: answers.ParQ4,
        ParQ5: answers.ParQ5,
        ParQ6: answers.ParQ6,
        ParQ7: answers.ParQ7,
      },
      { context: withoutForbiddenElevation(), withCredentials: true }
    );
  }
}
